/**
 * Housing Reports — /housing/reports
 * 3-tier hierarchy: Primary Output → Financial → Technical Documents
 * Decision-grade status badge at top.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, FileText, Download, Shield } from "lucide-react";
import { toast } from "sonner";
import { useHousingStatus } from "@/hooks/useHousingStatus";
import { useHousingJourney } from "@/hooks/useHousingJourney";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
function ReportCard({ title, tag, tagColor, description, endpoint, filename, method = "GET", body = null, accent, testId, badge, badgeColor }) {
  const [dl, setDl] = useState(false);

  const download = async () => {
    setDl(true);
    try {
      const token = localStorage.getItem("decarboniq_token");
      const opts  = { headers: { Authorization: `Bearer ${token}`, ...(method === "POST" ? { "Content-Type": "application/json" } : {}) } };
      if (method === "POST") opts.method = "POST";
      if (body) opts.body = JSON.stringify(body);
      const res  = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, opts);
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a    = document.createElement("a");
      a.href     = URL.createObjectURL(blob);
      a.download = filename;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success(`${title} downloaded.`);
    } catch { toast.error("Download failed — please retry."); }
    setDl(false);
  };

  return (
    <div className={`bg-white rounded-xl border-2 p-5 ${accent ? "border-teal-200" : "border-slate-200"}`}
      data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${accent ? "bg-teal-50" : "bg-slate-50"}`}>
          <FileText size={17} className={accent ? "text-teal-600" : "text-slate-600"} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            {badge && <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full border ${badgeColor}`}>{badge}</span>}
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">{description}</p>
        </div>
      </div>
      <button onClick={download} disabled={dl}
        className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-xl transition-colors disabled:opacity-50 ${
          accent ? "bg-[#0F172A] text-white hover:bg-slate-800" : "bg-slate-800 text-white hover:bg-slate-700"
        }`}
        data-testid={`download-${testId}`}>
        {dl ? <><RefreshCw size={14} className="animate-spin" /> Generating...</> : <><Download size={14} /> Download</>}
      </button>
    </div>
  );
}

function SectionHeader({ number, title, description, color }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}>
        <span className="text-xs font-bold text-white">{number}</span>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-600">{title}</p>
        {description && <p className="text-xs text-slate-400">{description}</p>}
      </div>
    </div>
  );
}

export default function HousingReports() {
  const navigate = useNavigate();
  const { review_status, stage, readiness, badgeClass, ringClass, statusIcon, readinessBadge, isActionRequired, isVerified, isBankable, nextStep } = useHousingStatus();
  const { loading, journey, demo, org, prog, kpis } = useHousingJourney();

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><RefreshCw size={18} className="animate-spin text-teal-500" /></div>;

  const rid  = journey?.report_id;

  const fbBody = {
    project_name:  "Coop Green Homes - 3-Bedroom Low-Carbon Demo Unit",
    developer_name: org.name || "Coop Green Homes Ltd", country: org.country || "Nigeria",
    brief_title: "HOUSING FINANCE BRIEF",
    embodied_reduction_tco2e: demo.embodied_reduction_tco2e || 1.64,
    operational_reduction_tco2e_yr: demo.operational_reduction_tco2e_yr || 1.13,
    total_lifetime_reduction_tco2e: demo.lifetime_reduction_tco2e || 35.49,
    project_life_years: 30, units_count: 1000,
    incremental_cost_usd: (demo.capex_usd || 4500) * 1000,
    cost_per_tco2e: demo.cost_per_tco2e || 126.8,
    energy_savings_yr: demo.energy_savings_usd_yr || 182.7,
    benchmark_position: "~20% lower embodied than conventional",
    mrv_readiness: "weak", operational_reduction_pct: 32, embodied_reduction_pct: 20,
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between">
        <PageHeader
          label="PROJECT WORKSPACE"
          title="Reports"
          subtitle="Two distinct documents for two different audiences"
          backUrl="/housing-project"
          backLabel="Housing Project"
        />
        {/* Status badge */}
        <div className={`rounded-xl border px-4 py-2.5 text-right flex-shrink-0 ${ringClass}`} data-testid="status-badge">
          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${badgeClass}`}>
            {review_status.toUpperCase()}
          </span>
          <p className="text-xs text-slate-500 mt-0.5">
            {isVerified ? "Ready for external use" : isActionRequired ? "Requires updates" : "Awaiting review"}
          </p>
          {nextStep && <p className="text-xs font-semibold text-slate-600 mt-0.5">→ {nextStep}</p>}
        </div>
      </div>

          {/* SECTION 1 — PRIMARY OUTPUT */}
          <div data-testid="section-primary">
            <SectionHeader number="1" title="Primary Output" description="Core credibility document — for regulators, DFI reviewers, and ESG teams" color="bg-teal-600" />
            <ReportCard
              title="Climate Credentials Report"
              tag="Primary Output"
              tagColor="bg-teal-50 text-teal-700 border-teal-200"
              description="Complete technical record: embodied + operational breakdown, DPCS-H v1.0 methodology, industry benchmark, SDG alignment, audit trail, QR verification. This is your primary climate credibility document."
              endpoint="/api/housing/credentials-report"
              filename="CoopGreenHomes-ClimateCredentials.pdf"
              accent testId="report-ccr"
              badge="Primary" badgeColor="bg-teal-100 text-teal-700 border-teal-200"
            />
          </div>

          {/* SECTION 2 — FINANCIAL */}
          <div data-testid="section-financial">
            <SectionHeader number="2" title="For Banks & Investors" description="Decision support for lenders and green finance pre-appraisal" color="bg-blue-600" />
            <ReportCard
              title="Finance Brief"
              tag="Financial"
              tagColor="bg-blue-50 text-blue-700 border-blue-200"
              description="Bank-facing: per-unit metrics, programme scaling to 1,000 units, capex/savings summary, financing readiness signal. For lender pre-appraisal and investment screening."
              endpoint="/api/housing/finance-brief-v2"
              filename="CoopGreenHomes-FinanceBrief-v2.pdf"
              testId="report-brief"
              badge="For Banks" badgeColor="bg-blue-100 text-blue-700 border-blue-200"
            />
          </div>

          {/* SECTION 3 — TECHNICAL DOCUMENTS */}
          <div data-testid="section-technical">
            <SectionHeader number="3" title="Technical Documentation" description="Supporting documents for carbon structuring, MRV, and validation" color="bg-slate-600" />
            <div className="space-y-3">
              <ReportCard title="Project Design Document (PDD)"
                description="Validation-grade: 15 sections covering baseline, additionality, methodology, MRV, SDGs. Verra/Gold Standard aligned structure."
                endpoint="/api/housing/pdd" filename="CoopGreenHomes-PDD-v0.1-Draft.pdf"
                testId="report-pdd" badge="Draft v0.1" badgeColor="bg-slate-100 text-slate-600 border-slate-200" />
              <ReportCard title="MRV Plan"
                description="15-section monitoring plan: parameter table, equipment specs, QA/QC, uncertainty management, audit trail integration."
                endpoint="/api/housing/mrv-plan" filename="CoopGreenHomes-MRVPlan.pdf"
                testId="report-mrv" />
              <ReportCard title="Emission Calculations"
                description="Step-by-step calculation detail with factor sources (IPCC/WSA/IEA), uncertainty ranges, and full programme totals."
                endpoint="/api/housing/emission-calculations" filename="CoopGreenHomes-EmissionCalc.pdf"
                testId="report-calcs" />
            </div>
          </div>

          {/* Article 6 Readiness Pack */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300">Readiness Template</span>
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Article 6 Pack</p>
            </div>
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-3">
              <p className="text-xs text-amber-700">Not currently eligible. This 7-document pack demonstrates required documentation structure for host country authorization. For readiness planning only.</p>
            </div>
            <ReportCard
              title="Article 6 Readiness Pack (7 documents)"
              description="Authorization Request · Host Country Alignment · Corresponding Adjustment · Sustainable Development Report · MRV Plan · Legal Declaration · PDD Summary"
              endpoint="/api/housing/article6-pack" filename="CoopGreenHomes-Article6ReadinessPack.pdf"
              testId="report-article6" badge="Readiness Template" badgeColor="bg-amber-100 text-amber-700 border-amber-200" />
          </div>

          {/* Verify link */}
          {rid && (
            <a href={`/verify/report/${rid}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-3 border border-slate-200 text-slate-600 text-xs font-semibold rounded-xl hover:bg-slate-50 bg-white w-full justify-center"
              data-testid="verify-link">
              <Shield size={13} /> Verify on Public Registry · {rid}
            </a>
          )}
    </div>
  );
}
